/**
 * iter160i — 3-Punkte-Menü an jeder Mietkonto-Buchung.
 * Bündelt alle möglichen Aktionen: umbuchen (Kautionskonto / andere MV),
 * Verlinkung aufheben, Vor-Eigentümer-Kennung, Löschen.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { LEDGER_CATEGORY_OPTIONS } from "@/lib/constants";
import { RateSelect } from "@/components/tenancy/RateSelect";
import { toast } from "sonner";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DotsThreeVertical, Clock, Bank, ArrowsLeftRight, LinkBreak, Trash, ArrowSquareOut, PencilSimple, Calculator, Scissors, ArrowsInLineHorizontal, CalendarBlank, LockSimple, HandCoins } from "@phosphor-icons/react";

export function PostingActionsMenu({ p, tenancyId, onDeleted, onChanged }) {
  const navigate = useNavigate();
  const [openMove, setOpenMove] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openSplit, setOpenSplit] = useState(false);
  const [openResplit, setOpenResplit] = useState(false);
  const [openPeriod, setOpenPeriod] = useState(false);
  const [openCategory, setOpenCategory] = useState(false);
  const [openInstallment, setOpenInstallment] = useState(false);
  const isIst = p.type === "ist";
  const isSoll = p.type === "soll";
  const canReassignDeposit = isIst && p.category !== "deposit";
  const canUnlink = isIst && !!p.bank_transaction_id;
  const canJumpToBank = isIst && !!p.bank_transaction_id;
  const canEdit = isSoll;  // 2026-03 — nur Sollstellungen editierbar
  // iter165 · Bug 5: Kategorie manuell ändern (nur Ist-Buchungen)
  const canChangeCategory = isIst;
  // 2026-09 · Split-Aktionen
  const canUnsplit = (p.split_info && p.split_info.split_count > 1);
  const canSplit = !p.split_info && Number(p.amount || 0) > 0.01;
  // 2026-10 · Ratenzahlung
  const canLinkInstallment = isIst && !p.linked_installment_plan_id;
  const isLinkedInstallment = isIst && !!p.linked_installment_plan_id;

  const jumpToBank = () => {
    // 2026-02 — Zur Bankbuchung springen: Bank-Modul mit Suche vorbelegen.
    navigate(`/bank?tx=${p.bank_transaction_id}`);
  };


  const togglePreOwnership = async () => {
    try {
      await api.patch(`/tenancies/${tenancyId}/postings/${p.id}/toggle-pre-ownership`,
        null, { params: { pre_ownership: !p.pre_ownership } });
      toast.success(p.pre_ownership ? "Buchung wieder aktiviert" : "Als Vor-Eigentümer-Buchung markiert");
      onChanged?.();
    } catch { toast.error("Konnte nicht umgeschaltet werden"); }
  };

  const reassignToDeposit = async () => {
    if (!await window._domivusConfirm(`Buchung "${p.reference || ""}" ins Kautionskonto umbuchen?`)) return;
    try {
      await api.post(`/deposit-accounts/${tenancyId}/reassign-to-deposit`, { posting_id: p.id });
      toast.success("Ins Kautionskonto umgebucht");
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Umbuchung fehlgeschlagen"); }
  };

  const unlinkFromBank = async () => {
    const isSplit = !!(p.split_info && p.split_info.split_count > 1);
    const msg = isSplit
      ? `Diese Buchung ist Teil einer aufgeteilten Bank-Zahlung (${p.split_info.split_count} Positionen · ${p.split_info.total_amount?.toLocaleString?.("de-DE", { minimumFractionDigits: 2 }) || ""} €).\n\nBeim Aufheben werden ALLE zugehörigen Positionen entfernt und die Bank-Buchung wird wieder frei verfügbar. Fortfahren?`
      : "Verknüpfung zur Bankbuchung wirklich aufheben? Die Bankzeile wird wieder als unzugeordnet markiert.";
    if (!await window._domivusConfirm(msg)) return;
    try {
      const { data } = await api.post(`/bank/transactions/${p.bank_transaction_id}/unlink`);
      if (data?.was_split) {
        toast.success(`Split-Gruppe aufgelöst · ${data.postings_removed_count} Positionen entfernt`);
      } else {
        toast.success("Verknüpfung aufgehoben");
      }
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Aufheben fehlgeschlagen"); }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="p-1 rounded hover:bg-slate-100 text-slate-500 hover:text-slate-900"
            data-testid={`posting-menu-${p.id}`}
            title="Aktionen"
          >
            <DotsThreeVertical size={18} weight="bold" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel className="text-[10px] uppercase text-slate-500">Aktionen</DropdownMenuLabel>
          {canEdit && (
            <DropdownMenuItem onClick={() => setOpenEdit(true)} data-testid={`menu-edit-${p.id}`}>
              <PencilSimple size={14} className="mr-2" />
              Sollstellung anpassen
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={() => setOpenPeriod(true)} data-testid={`menu-change-period-${p.id}`}>
            <CalendarBlank size={14} className="mr-2" />
            Monat ändern
          </DropdownMenuItem>
          {canChangeCategory && (
            <DropdownMenuItem onClick={() => setOpenCategory(true)} data-testid={`menu-change-category-${p.id}`}>
              <PencilSimple size={14} className="mr-2" />
              Kategorie ändern
            </DropdownMenuItem>
          )}
          {canSplit && (
            <DropdownMenuItem onClick={() => setOpenSplit(true)} data-testid={`menu-split-${p.id}`}>
              <Scissors size={14} className="mr-2" />
              In mehrere Anteile splitten
            </DropdownMenuItem>
          )}
          {canUnsplit && (
            <DropdownMenuItem onClick={() => setOpenResplit(true)} data-testid={`menu-resplit-${p.id}`}>
              <Scissors size={14} className="mr-2" />
              Split ändern
            </DropdownMenuItem>
          )}
          {canUnsplit && (
            <DropdownMenuItem
              onClick={async () => {
                if (!await window._domivusConfirm("Split aufheben und alle Anteile zu einer Buchung zusammenführen?")) return;
                try {
                  const r = await api.post(`/tenancies/${tenancyId}/postings/${p.id}/unsplit`);
                  toast.success(`Split aufgehoben (${r.data?.removed_count || 0} → 1 Buchung, ${r.data?.amount?.toFixed(2)?.replace('.', ',')} €)`);
                  onChanged?.();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Split konnte nicht aufgehoben werden");
                }
              }}
              data-testid={`menu-unsplit-${p.id}`}
            >
              <ArrowsInLineHorizontal size={14} className="mr-2" />
              Split aufheben
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={togglePreOwnership} data-testid={`menu-toggle-pre-own-${p.id}`}>
            <Clock size={14} className="mr-2" />
            {p.pre_ownership ? "Zurechnung aktivieren" : "Vor-Eigentümer zurechnen"}
          </DropdownMenuItem>
          {canLinkInstallment && (
            <DropdownMenuItem onClick={() => setOpenInstallment(true)} data-testid={`menu-link-installment-${p.id}`}>
              <HandCoins size={14} className="mr-2" />
              An Ratenzahlung anrechnen
            </DropdownMenuItem>
          )}
          {isLinkedInstallment && (
            <DropdownMenuItem
              onClick={async () => {
                if (!await window._domivusConfirm("Anrechnung auf Ratenzahlung aufheben?")) return;
                try {
                  await api.post(`/tenancies/${tenancyId}/postings/${p.id}/unlink-installment`);
                  toast.success("Anrechnung aufgehoben");
                  onChanged?.();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Aufheben fehlgeschlagen");
                }
              }}
              data-testid={`menu-unlink-installment-${p.id}`}
            >
              <LinkBreak size={14} className="mr-2" />
              Ratenzahlung-Anrechnung aufheben
            </DropdownMenuItem>
          )}
          {canReassignDeposit && (
            <DropdownMenuItem onClick={reassignToDeposit} data-testid={`menu-reassign-deposit-${p.id}`}>
              <Bank size={14} className="mr-2" />
              In Kautionskonto umbuchen
            </DropdownMenuItem>
          )}
          {isIst && (
            <DropdownMenuItem onClick={() => setOpenMove(true)} data-testid={`menu-move-tenancy-${p.id}`}>
              <ArrowsLeftRight size={14} className="mr-2" />
              In anderes Mietkonto umbuchen
            </DropdownMenuItem>
          )}
          {canJumpToBank && (
            <DropdownMenuItem onClick={jumpToBank} data-testid={`menu-jump-bank-${p.id}`}>
              <ArrowSquareOut size={14} className="mr-2" />
              Zur Bankbuchung springen
            </DropdownMenuItem>
          )}
          {canUnlink && (
            <DropdownMenuItem onClick={unlinkFromBank} data-testid={`menu-unlink-${p.id}`}>
              <LinkBreak size={14} className="mr-2" />
              Bank-Verknüpfung aufheben
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={onDeleted}
            data-testid={`menu-delete-${p.id}`}
            className="text-rose-700 focus:text-rose-800 focus:bg-rose-50"
          >
            <Trash size={14} className="mr-2" />
            Buchung löschen
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <MoveToOtherTenancyDialog
        open={openMove}
        onClose={() => setOpenMove(false)}
        posting={p}
        currentTenancyId={tenancyId}
        onChanged={onChanged}
      />
      <EditPostingDialog
        open={openEdit}
        onClose={() => setOpenEdit(false)}
        posting={p}
        tenancyId={tenancyId}
        onChanged={onChanged}
      />
      <SplitPostingDialog
        open={openSplit}
        onClose={() => setOpenSplit(false)}
        posting={p}
        tenancyId={tenancyId}
        onChanged={onChanged}
      />
      <SplitPostingDialog
        open={openResplit}
        onClose={() => setOpenResplit(false)}
        posting={p}
        tenancyId={tenancyId}
        onChanged={onChanged}
        mode="edit"
      />
      <ChangePeriodDialog
        open={openPeriod}
        onClose={() => setOpenPeriod(false)}
        posting={p}
        tenancyId={tenancyId}
        onChanged={onChanged}
      />
      <ChangeCategoryDialog
        open={openCategory}
        onClose={() => setOpenCategory(false)}
        posting={p}
        tenancyId={tenancyId}
        onChanged={onChanged}
      />
      <LinkInstallmentDialog
        open={openInstallment}
        onClose={() => setOpenInstallment(false)}
        posting={p}
        tenancyId={tenancyId}
        onChanged={onChanged}
      />
    </>
  );
}

function MoveToOtherTenancyDialog({ open, onClose, posting, currentTenancyId, onChanged }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);

  const search = async (query) => {
    setQ(query);
    if (query.trim().length < 2) { setResults([]); return; }
    try {
      const { data } = await api.get(`/tenancies`, { params: { search: query, limit: 20 } });
      const items = (data?.items || data || []).filter((t) => t.id !== currentTenancyId);
      setResults(items);
    } catch { setResults([]); }
  };

  const doMove = async (targetId) => {
    setBusy(true);
    try {
      await api.post(`/tenancies/${currentTenancyId}/postings/${posting.id}/move`,
        { target_tenancy_id: targetId });
      toast.success("Buchung ins Ziel-Mietkonto verschoben");
      onClose();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Verschieben fehlgeschlagen");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Buchung in anderes Mietkonto umbuchen</DialogTitle></DialogHeader>
        <div className="text-sm bg-slate-50 border rounded p-2">
          <div className="text-xs text-slate-500">Buchung</div>
          <div className="font-medium">{posting.reference || "—"}</div>
          <div className="text-xs text-slate-500 mt-1">
            {posting.posting_date} · {(posting.haben ?? posting.amount ?? 0).toLocaleString("de-DE", { minimumFractionDigits: 2 })} €
          </div>
        </div>
        <div>
          <label className="text-xs text-slate-700">Ziel-Mietverhältnis suchen</label>
          <input
            type="text"
            value={q}
            onChange={(e) => search(e.target.value)}
            placeholder="Vertragsnr., Mieter oder Objekt…"
            className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            data-testid="move-tenancy-search"
          />
        </div>
        <div className="max-h-60 overflow-y-auto border rounded">
          {results.length === 0 && q.length >= 2 && (
            <div className="p-3 text-xs text-slate-500">Keine Treffer</div>
          )}
          {results.map((t) => (
            <button
              key={t.id}
              type="button"
              disabled={busy}
              onClick={() => doMove(t.id)}
              className="w-full text-left px-3 py-2 text-xs hover:bg-slate-50 border-b last:border-0 disabled:opacity-50"
              data-testid={`move-tenancy-option-${t.id}`}
            >
              <div className="font-medium">{t.contract_number} · {(t.tenant_names || []).join(", ")}</div>
              <div className="text-slate-500">{t.unit_label}</div>
            </button>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


const POSTING_CATEGORY_OPTIONS = LEDGER_CATEGORY_OPTIONS;

function _daysInMonth(y, m) { return new Date(y, m, 0).getDate(); }
function _parseDateSafe(s) {
  if (!s) return null;
  const [y, m, d] = s.split("-").map((x) => parseInt(x, 10));
  if (!y || !m || !d) return null;
  return { y, m, d };
}

function EditPostingDialog({ open, onClose, posting, tenancyId, onChanged }) {
  const [amount, setAmount] = useState("");
  const [postingDate, setPostingDate] = useState("");
  const [category, setCategory] = useState("");
  const [reference, setReference] = useState("");
  const [helper, setHelper] = useState("none");
  const [helperDays, setHelperDays] = useState("");
  const [helperPct, setHelperPct] = useState("");
  const [busy, setBusy] = useState(false);
  const originalAmount = parseFloat(posting?.soll ?? posting?.amount ?? 0);
  const original = posting?.original_snapshot;

  useEffect(() => {
    if (open) {
      setAmount(String(originalAmount));
      setPostingDate(posting?.posting_date || "");
      setCategory(posting?.category || "rent_cold");
      setReference(posting?.reference || "");
      setHelper("none"); setHelperDays(""); setHelperPct("");
    }
    // eslint-disable-next-line
  }, [open]);

  const base = originalAmount || 0;

  const applyHelper = () => {
    let calc = base;
    let note = "";
    if (helper === "days") {
      const d = _parseDateSafe(postingDate);
      const daysInMonth = d ? _daysInMonth(d.y, d.m) : 30;
      const usedDays = parseInt(helperDays || "0", 10) || 0;
      if (usedDays > 0 && usedDays <= daysInMonth) {
        calc = (base * usedDays) / daysInMonth;
        note = `anteilig ${usedDays}/${daysInMonth} Tage`;
      }
    } else if (helper === "half") { calc = base / 2; note = "hälftig (½)"; }
    else if (helper === "third") { calc = base / 3; note = "Drittel (⅓)"; }
    else if (helper === "quarter") { calc = base / 4; note = "Viertel (¼)"; }
    else if (helper === "reduce_pct") {
      const pct = parseFloat(helperPct || "0") || 0;
      calc = base * (1 - pct / 100);
      note = `${pct}% Ausfall/Reduzierung`;
    } else if (helper === "increase_pct") {
      const pct = parseFloat(helperPct || "0") || 0;
      calc = base * (1 + pct / 100);
      note = `${pct}% Erhöhung`;
    }
    calc = Math.round(calc * 100) / 100;
    setAmount(String(calc));
    if (note) {
      const cur = reference || "";
      const nextRef = cur.includes(note) ? cur : (cur ? cur + " · " + note : note);
      setReference(nextRef);
    }
  };

  const save = async () => {
    if (!amount || parseFloat(amount) < 0) { toast.error("Bitte gültigen Betrag eingeben"); return; }
    setBusy(true);
    try {
      await api.patch(`/tenancies/${tenancyId}/postings/${posting.id}`, {
        amount: parseFloat(amount),
        posting_date: postingDate || undefined,
        category: category || undefined,
        reference: reference || undefined,
      });
      toast.success("Sollstellung angepasst");
      onClose(); onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally { setBusy(false); }
  };

  const revert = async () => {
    if (!await window._domivusConfirm("Original-Werte wiederherstellen?")) return;
    setBusy(true);
    try {
      await api.post(`/tenancies/${tenancyId}/postings/${posting.id}/revert`);
      toast.success("Original wiederhergestellt");
      onClose(); onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Wiederherstellen fehlgeschlagen");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="edit-posting-dialog">
        <DialogHeader><DialogTitle>Sollstellung anpassen</DialogTitle></DialogHeader>
        {original && (
          <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-900" data-testid="edit-posting-snapshot">
            Bereits geändert · Original: {Number(original.amount ?? 0).toLocaleString("de-DE", { minimumFractionDigits: 2 })} € · {original.posting_date}
            <button type="button" onClick={revert} className="ml-2 underline hover:no-underline" data-testid="edit-posting-revert">
              Original wiederherstellen
            </button>
          </div>
        )}
        <div className="space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Betrag (€)</Label>
              <Input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)}
                     data-testid="edit-posting-amount" />
            </div>
            <div>
              <Label className="text-xs">Fälligkeit</Label>
              <Input type="date" value={postingDate} onChange={(e) => setPostingDate(e.target.value)}
                     data-testid="edit-posting-date" />
            </div>
          </div>
          <div>
            <Label className="text-xs">Kategorie</Label>
            <select value={category} onChange={(e) => setCategory(e.target.value)}
                    className="w-full h-9 rounded border border-slate-200 bg-white px-2 text-sm"
                    data-testid="edit-posting-category">
              {POSTING_CATEGORY_OPTIONS.map(([k, l]) => (<option key={k} value={k}>{l}</option>))}
            </select>
          </div>
          <div>
            <Label className="text-xs">Notiz / Referenz</Label>
            <Input value={reference} onChange={(e) => setReference(e.target.value)}
                   data-testid="edit-posting-reference" />
          </div>

          <div className="border-t border-slate-100 pt-2">
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold flex items-center gap-1 mb-2">
              <Calculator size={12} /> Rechenhilfen (basierend auf {base.toLocaleString("de-DE", { minimumFractionDigits: 2 })} €)
            </div>
            <div className="grid grid-cols-2 gap-2">
              <select value={helper} onChange={(e) => setHelper(e.target.value)}
                      className="h-9 rounded border border-slate-200 bg-white px-2 text-sm col-span-2"
                      data-testid="edit-posting-helper">
                <option value="none">— Rechenhilfe wählen —</option>
                <option value="days">Tageweise anteilig</option>
                <option value="half">Hälftig (½)</option>
                <option value="third">Drittel (⅓)</option>
                <option value="quarter">Viertel (¼)</option>
                <option value="reduce_pct">Ausfall/Reduzierung um %</option>
                <option value="increase_pct">Erhöhung um %</option>
              </select>
              {helper === "days" && (
                <div className="col-span-2 flex items-center gap-2">
                  <Input type="number" min="1" max="31" placeholder="Tage im Monat"
                         value={helperDays} onChange={(e) => setHelperDays(e.target.value)}
                         data-testid="edit-posting-helper-days" />
                  <span className="text-xs text-slate-500">von {(() => {
                    const d = _parseDateSafe(postingDate);
                    return d ? _daysInMonth(d.y, d.m) : 30;
                  })()}</span>
                </div>
              )}
              {(helper === "reduce_pct" || helper === "increase_pct") && (
                <div className="col-span-2 flex items-center gap-2">
                  <Input type="number" step="0.1" placeholder="Prozentsatz"
                         value={helperPct} onChange={(e) => setHelperPct(e.target.value)}
                         data-testid="edit-posting-helper-pct" />
                  <span className="text-xs text-slate-500">%</span>
                </div>
              )}
              {helper !== "none" && (
                <div className="col-span-2">
                  <Button size="sm" variant="outline" onClick={applyHelper} className="w-full"
                          data-testid="edit-posting-helper-apply">
                    Wert übernehmen
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
          <Button onClick={save} disabled={busy}
                  className="bg-teal-700 hover:bg-teal-800"
                  data-testid="edit-posting-save">
            {busy ? "Speichert…" : "Speichern"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ---------------------------------------------------------------------------
// 2026-09 · Buchung splitten
// ---------------------------------------------------------------------------
const _r2 = (v) => Math.round(Number(v || 0) * 100) / 100;
const _fmtEur = (v) => Number(v || 0).toFixed(2).replace(".", ",");

function SplitCategorySelect({ value, onChange, isIst, relevant, testId }) {
  const rel = LEDGER_CATEGORY_OPTIONS.filter(([k]) => relevant.includes(k));
  const rest = LEDGER_CATEGORY_OPTIONS.filter(([k]) => !relevant.includes(k));
  return (
    <select className="col-span-3 h-9 border border-slate-300 rounded-md px-2 text-sm" value={value}
            onChange={(e) => onChange(e.target.value)} data-testid={testId}>
      {isIst && <option value="_auto">Auto-Verrechnung (Rückstände auffüllen)</option>}
      {isIst && <option value="_installment">Ratenzahlung (auf Ratenplan)</option>}
      {isIst && <option value="surplus">Überschuss / Guthaben</option>}
      {rel.length > 0 && (
        <optgroup label="Passend zum Mietverhältnis">
          {rel.map(([k, l]) => (<option key={k} value={k}>{l}</option>))}
        </optgroup>
      )}
      <optgroup label={rel.length > 0 ? "Weitere Kategorien" : "Kategorien"}>
        {rest.map(([k, l]) => (<option key={k} value={k}>{l}</option>))}
      </optgroup>
    </select>
  );
}

// Letzte Zeile nimmt die Differenz auf; wird die letzte bearbeitet, passt sich die vorletzte an.
function rebalanceParts(parts, idx, total) {
  const n = parts.length;
  if (n < 2) return parts;
  const target = idx === n - 1 ? n - 2 : n - 1;
  const others = parts.reduce((s, r, j) => (j === target ? s : s + Number(r.amount || 0)), 0);
  return parts.map((r, j) => (j === target ? { ...r, amount: _r2(total - others) } : r));
}

function SplitPostingDialog({ open, onClose, posting, tenancyId, onChanged, mode = "new" }) {
  const [parts, setParts] = useState([]);
  const [suggestion, setSuggestion] = useState(null);
  const [rate, setRate] = useState(null);
  const [busy, setBusy] = useState(false);
  const isIst = posting?.type === "ist";
  const isEdit = mode === "edit";

  useEffect(() => {
    if (!open || !posting?.id) return;
    setSuggestion(null);
    api.get(`/tenancies/${tenancyId}/postings/${posting.id}/split-suggestion`)
      .then(({ data }) => {
        setSuggestion(data);
        setParts(isEdit && data.current_parts?.length ? data.current_parts : data.parts);
      })
      .catch(() => {
        const half = _r2(Number(posting.amount || 0) / 2);
        setParts([{ category: "rent_cold", amount: half }, { category: "utilities", amount: _r2(posting.amount - half) }]);
      });
  }, [open, posting?.id, posting?.amount, tenancyId, isEdit]);

  const orig = _r2(suggestion?.total ?? posting?.amount);
  const relevant = suggestion?.relevant_categories || [];
  const total = parts.reduce((s, r) => s + Number(r.amount || 0), 0);
  const diff = _r2(total - orig);
  const positive = parts.filter((r) => Number(r.amount || 0) > 0);
  const enoughParts = positive.length >= 2 || positive.some((r) => r.category === "_auto");
  const ok = Math.abs(diff) < 0.005 && enoughParts;

  const setAmount = (i, val) => setParts((s) => rebalanceParts(
    s.map((x, j) => (j === i ? { ...x, amount: val } : x)), i, orig));
  const setCategory = (i, cat) => setParts((s) => s.map((x, j) => (j === i ? { ...x, category: cat } : x)));
  const removePart = (i) => setParts((s) => {
    const next = s.filter((_, j) => j !== i);
    return next.length >= 2 ? rebalanceParts(next, 0, orig) : next;
  });

  const submit = async () => {
    if (!ok) { toast.error(enoughParts ? `Summe (${_fmtEur(total)} €) muss ${_fmtEur(orig)} € ergeben.` : "Mindestens 2 Anteile mit Betrag > 0 erforderlich."); return; }
    setBusy(true);
    try {
      await api.post(`/tenancies/${tenancyId}/postings/${posting.id}/${isEdit ? "resplit" : "split"}`, {
        parts: parts.filter((r) => Number(r.amount || 0) > 0).map((r) => ({
          category: r.category, amount: Number(r.amount),
          period: posting.period || null, reference: posting.reference || null,
          ...(r.category === "_installment" ? { installment_plan_id: rate?.plan_id, installment_index: rate?.index } : {}),
        })),
      });
      toast.success(isEdit ? "Split geändert" : "Buchung gesplittet");
      onChanged?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Split fehlgeschlagen");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="split-posting-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Split ändern" : "Buchung splitten"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2 text-xs text-slate-600">
            <span>
              {isEdit ? "Gesamtbetrag" : "Ursprung"}: <span className="font-mono" data-testid="split-total">{_fmtEur(orig)} €</span> · {posting?.reference || "Buchung"}
            </span>
            {suggestion?.parts?.length > 0 && (
              <button type="button" className="text-teal-700 hover:underline whitespace-nowrap"
                      onClick={() => setParts(suggestion.parts)} data-testid="split-apply-suggestion">
                Vorschlag übernehmen
              </button>
            )}
          </div>
          {parts.map((r, i) => (
            <div key={i} className="grid grid-cols-6 gap-2 items-center">
              <SplitCategorySelect value={r.category} onChange={(c) => setCategory(i, c)} isIst={isIst}
                                   relevant={relevant} testId={`split-cat-${i}`} />
              <Input type="number" step="0.01" value={r.amount}
                     onChange={(e) => setAmount(i, e.target.value)}
                     className="col-span-2 h-9 text-right font-mono" data-testid={`split-amt-${i}`} />
              <button type="button" disabled={parts.length <= 2} onClick={() => removePart(i)}
                      className="text-slate-400 hover:text-rose-600 disabled:opacity-30 justify-self-center"
                      title="Anteil entfernen" data-testid={`split-remove-${i}`}>
                <Trash size={14} />
              </button>
            </div>
          ))}
          <div className="text-[11px] text-slate-500 italic">
            Die letzte Zeile nimmt automatisch die Differenz auf (bearbeiten Sie die letzte Zeile, passt sich die vorletzte an).
          </div>
          {parts.some((r) => r.category === "_installment") && (
            <RateSelect tenancyId={tenancyId} value={rate} onChange={setRate} testId="split-rate-select" />
          )}
          {parts.some((r) => r.category === "_auto") && (
            <div className="text-[11px] text-slate-500 italic" data-testid="split-auto-hint">
              Auto-Verrechnung: füllt zuerst den Zielmonat, dann ältere Rückstände (Kaltmiete zuerst); ein Rest wird als Überschuss/Guthaben gebucht.
            </div>
          )}
          <div className="flex items-center justify-between text-xs pt-2">
            <button className="text-teal-700 hover:underline" data-testid="split-add-part"
                    onClick={() => setParts((s) => [...s, { category: isIst ? "surplus" : "other", amount: 0 }])}>
              + Anteil hinzufügen
            </button>
            <span className={ok ? "text-slate-600" : "text-rose-700 font-semibold"} data-testid="split-sum">
              Summe: {_fmtEur(total)} € {ok ? "· ✓" : Math.abs(diff) >= 0.005 ? `· Abweichung ${_fmtEur(diff)} €` : "· mind. 2 Anteile > 0 €"}
            </span>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Abbrechen</Button>
          <Button onClick={submit} disabled={busy || !ok || parts.length === 0} className="bg-teal-600 hover:bg-teal-700" data-testid="split-submit">
            {busy ? "Speichert…" : isEdit ? "Split speichern" : "Splitten"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ---------------------------------------------------------------------------
// 2026-09 · Monat ändern
// ---------------------------------------------------------------------------
function ChangePeriodDialog({ open, onClose, posting, tenancyId, onChanged }) {
  const [period, setPeriod] = useState(posting?.period || "");
  const [busy, setBusy] = useState(false);
  const isIst = posting?.type === "ist";
  useEffect(() => { if (open) setPeriod(posting?.period || ""); }, [open, posting?.period]);

  const submit = async () => {
    if (!/^\d{4}-\d{2}$/.test(period)) { toast.error("Format: YYYY-MM (z. B. 2026-09)"); return; }
    setBusy(true);
    try {
      await api.patch(`/tenancies/${tenancyId}/postings/${posting.id}`, { period });
      toast.success(isIst ? "Monat manuell fixiert" : "Monat-Zuordnung aktualisiert");
      onChanged?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Änderung fehlgeschlagen");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="change-period-dialog">
        <DialogHeader>
          <DialogTitle>Monat-Zuordnung ändern</DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          <div className="text-xs text-slate-600">
            Aktuell: <b>{posting?.period || "—"}</b>
            {isIst && posting?.period_source === "manual" && (
              <span className="ml-1 text-indigo-700">· manuell fixiert</span>
            )}
          </div>
          <Label className="text-xs">Neuer Monat (YYYY-MM)</Label>
          <Input
            type="month"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="h-9"
            data-testid="change-period-input"
          />
          <div className="text-[11px] text-slate-500 italic">
            {isIst
              ? "Ist-Buchung: Wird als manuell fixiert markiert und nicht mehr durch Bank-Refresh überschrieben."
              : "Zugeordnete Zahlungen werden mitverschoben (applied_to_period wird aktualisiert)."}
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Abbrechen</Button>
          <Button onClick={submit} disabled={busy} className="bg-teal-600 hover:bg-teal-700" data-testid="change-period-submit">
            {busy ? "Speichert…" : "Übernehmen"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ---------------------------------------------------------------------------
// iter165 · Dialog: Ist-Buchung Kategorie manuell ändern (Bug 5)
// ---------------------------------------------------------------------------
const CATEGORY_OPTIONS = [
  ...LEDGER_CATEGORY_OPTIONS.map(([value, label]) => ({ value, label })),
  { value: "surplus", label: "Überschuss / Guthaben" },
];

function ChangeCategoryDialog({ open, onClose, posting, tenancyId, onChanged }) {
  const [category, setCategory] = useState(posting?.category || "rent_cold");
  const [rate, setRate] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setCategory(posting?.category || "rent_cold"); setRate(null); } },
    [open, posting?.category]);

  const submit = async () => {
    if (!category) { toast.error("Bitte Kategorie wählen"); return; }
    setBusy(true);
    try {
      if (category === "_auto") {
        await api.post(`/tenancies/${tenancyId}/postings/${posting.id}/split`, {
          parts: [{ category: "_auto", amount: Number(posting.amount), reference: posting.reference || null }],
        });
        toast.success("Neu verteilt: offene Posten des Monats/Rückstände zuerst, Rest als Überschuss");
      } else if (category === "_installment") {
        if (!rate?.plan_id) { toast.error("Kein aktiver Ratenplan"); setBusy(false); return; }
        await api.post(`/tenancies/${tenancyId}/postings/${posting.id}/split`, {
          parts: [{ category: "_installment", amount: Number(posting.amount), reference: posting.reference || null,
                    installment_plan_id: rate.plan_id, installment_index: rate.index }],
        });
        toast.success(`Auf Rate ${rate.index} angerechnet und auf die Rückstände verteilt`);
      } else {
        await api.patch(`/tenancies/${tenancyId}/postings/${posting.id}`, { category });
        toast.success("Kategorie aktualisiert");
      }
      onChanged?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Änderung fehlgeschlagen");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="change-category-dialog">
        <DialogHeader>
          <DialogTitle>Kategorie ändern</DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          <div className="text-xs text-slate-600">
            Aktuell: <b>{
              (CATEGORY_OPTIONS.find((o) => o.value === posting?.category) || {}).label
              || posting?.category || "—"
            }</b>
            {posting?.category_locked && (
              <span className="ml-1 text-indigo-700">· manuell fixiert</span>
            )}
          </div>
          <Label className="text-xs">Neue Kategorie</Label>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="w-full h-9 border rounded px-2 text-sm bg-white"
            data-testid="change-category-select"
          >
            {posting?.type === "ist" && Number(posting?.amount) > 0 && (
              <option value="_auto">Auto-Verrechnung (neu verteilen)</option>
            )}
            {posting?.type === "ist" && Number(posting?.amount) > 0 && (
              <option value="_installment">Ratenzahlung (auf Ratenplan)</option>
            )}
            {CATEGORY_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          {category === "_installment" && (
            <RateSelect tenancyId={tenancyId} value={rate} onChange={setRate} testId="change-category-rate-select" />
          )}
          <div className="text-[11px] text-slate-500 italic">
            Kategorie wird als manuell fixiert markiert und nicht mehr durch
            Bank-Refresh überschrieben. Nützlich, wenn der Wasserfall die Zahlung
            versehentlich falsch klassifiziert hat.
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Abbrechen</Button>
          <Button onClick={submit} disabled={busy}
                  className="bg-teal-600 hover:bg-teal-700"
                  data-testid="change-category-submit">
            {busy ? "Speichert…" : "Übernehmen"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}



// ---------------------------------------------------------------------------
// 2026-10 · Dialog: Ist-Buchung auf eine Rate eines Ratenplans anrechnen
// ---------------------------------------------------------------------------
function LinkInstallmentDialog({ open, onClose, posting, tenancyId, onChanged }) {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setSelectedPlanId("");
    setSelectedIndex(null);
    api.get(`/tenancies/${tenancyId}/installment-plans`)
      .then(({ data }) => {
        const items = data?.items || [];
        setPlans(items);
        // Vorauswahl: aktivster Plan + nächste offene Rate
        const active = items.find((p) => p.status === "active") || items[0];
        if (active) {
          setSelectedPlanId(active.id);
          const nextRate = (active.installments || []).find((i) => i.status !== "paid");
          if (nextRate) setSelectedIndex(nextRate.index);
        }
      })
      .catch(() => toast.error("Ratenpläne konnten nicht geladen werden"))
      .finally(() => setLoading(false));
  }, [open, tenancyId]);

  const submit = async () => {
    if (!selectedPlanId || selectedIndex == null) {
      toast.error("Bitte Plan und Rate wählen");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/tenancies/${tenancyId}/postings/${posting.id}/link-installment`,
        { plan_id: selectedPlanId, installment_index: selectedIndex });
      toast.success("Buchung auf Rate angerechnet");
      onChanged?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Anrechnung fehlgeschlagen");
    } finally { setBusy(false); }
  };

  const activePlan = plans.find((p) => p.id === selectedPlanId);
  const amount = Number(posting?.amount || 0);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl" data-testid="link-installment-dialog">
        <DialogHeader>
          <DialogTitle>An Ratenzahlung anrechnen</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-xs text-slate-600">
            Zahlungseingang: <b className="tabular-nums">{amount.toFixed(2).replace(".", ",")} €</b>
          </div>
          {loading && <div className="text-sm text-slate-500">Lädt Ratenpläne…</div>}
          {!loading && plans.length === 0 && (
            <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-3">
              Kein Ratenplan für dieses Mietverhältnis vorhanden. Lege zunächst über die Zahlungsaufforderungen einen Ratenplan an.
            </div>
          )}
          {!loading && plans.length > 0 && (
            <>
              <div>
                <Label className="text-xs">Ratenplan</Label>
                <select
                  value={selectedPlanId}
                  onChange={(e) => { setSelectedPlanId(e.target.value); setSelectedIndex(null); }}
                  className="w-full h-9 rounded-md border border-slate-200 bg-white text-sm px-2 mt-1"
                  data-testid="installment-plan-select"
                >
                  {plans.map((p) => (
                    <option key={p.id} value={p.id}>
                      {new Date(p.agreed_at).toLocaleDateString("de-DE")} · {Number(p.total_arrears_eur).toFixed(2).replace(".", ",")} € · {p.status}
                    </option>
                  ))}
                </select>
              </div>
              {activePlan && (
                <div>
                  <Label className="text-xs">Rate wählen</Label>
                  <div className="mt-1 border border-slate-200 rounded divide-y divide-slate-100 max-h-64 overflow-y-auto">
                    {(activePlan.installments || []).map((i) => {
                      const paid = Number(i.paid_amount_eur || 0);
                      const total = Number(i.amount_eur || 0);
                      const open = Math.max(0, total - paid);
                      const isSelected = i.index === selectedIndex;
                      return (
                        <button
                          key={i.index}
                          type="button"
                          onClick={() => setSelectedIndex(i.index)}
                          className={`w-full text-left px-3 py-2 flex items-center justify-between gap-3 text-sm ${
                            isSelected ? "bg-teal-50" : "hover:bg-slate-50"
                          }`}
                          data-testid={`installment-rate-${i.index}`}
                        >
                          <div className="flex items-center gap-2">
                            <span className={`inline-flex items-center justify-center h-5 w-5 rounded-full border text-[10px] ${
                              isSelected ? "border-teal-600 bg-teal-600 text-white" : "border-slate-300 text-slate-500"
                            }`}>{i.index}</span>
                            <div>
                              <div className="font-medium text-slate-900">Rate {i.index}</div>
                              <div className="text-[11px] text-slate-500">fällig {new Date(i.due_date).toLocaleDateString("de-DE")} · Status {i.status}</div>
                            </div>
                          </div>
                          <div className="text-right tabular-nums">
                            <div className="text-slate-700">{total.toFixed(2).replace(".", ",")} €</div>
                            {paid > 0 && <div className="text-[11px] text-slate-500">bezahlt {paid.toFixed(2).replace(".", ",")} · offen {open.toFixed(2).replace(".", ",")} €</div>}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              <div className="text-[11px] text-slate-500 italic">
                Die Zahlung wird auf die gewählte Rate angerechnet. Ist die Rate danach vollständig bezahlt, wird sie automatisch als „bezahlt" markiert. Alle Raten bezahlt → Plan schließt automatisch.
              </div>
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Abbrechen</Button>
          <Button
            onClick={submit}
            disabled={busy || !selectedPlanId || selectedIndex == null}
            className="bg-teal-600 hover:bg-teal-700"
            data-testid="link-installment-submit"
          >
            {busy ? "Rechne an…" : "Anrechnen"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}




// ---------------------------------------------------------------------------
// 2026-10 · Monats-Badge mit Auto/Manuell-Kennung + Inline-Änderung
// ---------------------------------------------------------------------------
const _MONTH_NAMES = ["Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"];

function _formatPeriod(period) {
  if (!period) return "—";
  const [y, m] = String(period).split("-");
  const idx = parseInt(m || "0", 10) - 1;
  return `${_MONTH_NAMES[idx] || m} ${y}`;
}

export function PeriodBadge({ p, tenancyId, onChanged }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const isManual = p.period_source === "manual";
  const isIst = p.type === "ist";

  if (!p.period) {
    return (
      <>
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="ml-1.5 inline-flex items-center gap-0.5 rounded px-1 py-0.5 text-[9px] font-medium text-slate-400 hover:text-teal-700 hover:bg-teal-50"
          title="Kein Monat zugeordnet — klicken, um manuell zuzuordnen"
          data-testid={`posting-period-badge-${p.id}`}
        >
          <CalendarBlank size={9} /> Monat wählen
        </button>
        <ChangePeriodDialog
          open={open}
          onClose={() => setOpen(false)}
          posting={p}
          tenancyId={tenancyId}
          onChanged={onChanged}
        />
      </>
    );
  }

  const badgeCls = isManual
    ? "ml-1.5 inline-flex items-center gap-0.5 rounded px-1 py-0.5 text-[9px] font-medium text-indigo-700 hover:bg-indigo-50"
    : "ml-1.5 inline-flex items-center gap-0.5 rounded px-1 py-0.5 text-[9px] font-medium text-slate-500 hover:bg-slate-100 hover:text-slate-800";

  const title = isManual
    ? `Monat manuell fixiert: ${_formatPeriod(p.period)} · Klicken zum Ändern`
    : `Automatisch zugeordnet: ${_formatPeriod(p.period)} · Klicken zum manuellen Ändern`;

  const unlock = async (e) => {
    e.stopPropagation();
    if (busy) return;
    if (!await window._domivusConfirm("Manuelle Monats-Fixierung aufheben? Die Buchung wird beim nächsten Bank-Refresh wieder automatisch zugeordnet.")) return;
    setBusy(true);
    try {
      await api.post(`/tenancies/${tenancyId}/postings/${p.id}/unlock-period`);
      toast.success("Monat wieder automatisch zugeordnet");
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Aufheben fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={badgeCls}
        title={title}
        data-testid={`posting-period-badge-${p.id}`}
      >
        {_formatPeriod(p.period)}
        {isManual && isIst && (
          <span
            role="button"
            onClick={unlock}
            className="ml-0.5 -mr-0.5 inline-flex items-center hover:text-indigo-950"
            title="Manuell fixiert — klicken zum Aufheben (Auto)"
            data-testid={`posting-period-unlock-${p.id}`}
          >
            <LockSimple size={9} weight="fill" />
          </span>
        )}
      </button>
      <ChangePeriodDialog
        open={open}
        onClose={() => setOpen(false)}
        posting={p}
        tenancyId={tenancyId}
        onChanged={onChanged}
      />
    </>
  );
}
